import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TeamRemoteControllerService } from 'core/api';
import { ITeamDto } from 'core/dto';
import { subjectFromObs } from 'core/rx-js';
import { LoaderService, MessageBoxService, ScreenUtilsService, SnackbarService } from 'core/services';
import { SharedComponentsModule } from 'core/shared-components';
import { BehaviorSubject, Subject, debounceTime, filter, map, of, startWith, switchMap, take } from 'rxjs';
import { designerPaths } from '../../designer.paths';
import { DsgDeepTranslationKey, DsgTranslatePipe } from '../../pipes/translate.pipe';
import { MobileToolbarContentDirective } from '../../root/sidenav/mobile-toolbar-content.directive';
import { DataGridComponent, IColumn } from '../../shared-components/data-grid/data-grid.component';
import { DataGridModule } from '../../shared-components/data-grid/data-grid.module';
import { LoadFn } from '../../shared-components/data-grid/data-grid.service';
import { PageBaseDirective } from '../../shared-components/page-base.directive';

@Component({
  selector: 'ss-team-management',
  standalone: true,
  imports: [
    CommonModule,
    DataGridModule,
    MatIconModule,
    MatButtonModule,
    DsgTranslatePipe,
    SharedComponentsModule,
    MobileToolbarContentDirective
  ],
  host: {
    class: 'display-block padding-standard full-height'
  },
  hostDirectives: [PageBaseDirective],

  templateUrl: './team-management.component.html',
  styleUrls: ['./team-management.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TeamManagementComponent {

  readonly showSearchSubject = new BehaviorSubject<boolean>(false);

  @ViewChild('gridRef')
  private _gridRef: DataGridComponent

  readonly columns: IColumn[] = [
    {
      caption: 'ss-entities.team.name',
      dataField: 'name',
      type: 'string',
      title: true
    }
  ]

  readonly #destroy = new Subject<void>()

  readonly refreshSubject = new Subject<void>();
  readonly teamsSubject = subjectFromObs({
    initialValue: null,
    observable: this.refreshSubject.pipe(
      startWith(null),
      switchMap(() => {

        const id = this._loaderService.show({ key: 'teams.get', text: 'ss-general.loading' });
        return this._teamRemoteControllerService.getMany({ page: 0, size: 10000, sort: ['name'] }).pipe(
          this._loaderService.disposeLoader(id),
          this._loaderService.disposeByKey('teams.get')
        )
      })
    ),
    until: this.#destroy
  })

  readonly loadFnObs = this.teamsSubject.pipe(
    filter(Boolean),
    debounceTime(250),
    map((teams): LoadFn => (skip, take, search) => {
      const filteredTeams = teams.filter((team) => !search || team.name.toLowerCase().includes(search.toLowerCase()))

      return of({
        data: filteredTeams.filter((_, idx) => idx >= skip && idx < (skip + take)),
        totalCount: filteredTeams.length
      })
    }


    )
  )

  readonly isSmallerScreenObs = this._screenUtilsService.isSmallerScreenObs;

  constructor(
    private readonly _router: Router,
    private readonly _teamRemoteControllerService: TeamRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _messageBoxService: MessageBoxService<DsgDeepTranslationKey>,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,
    private readonly _screenUtilsService: ScreenUtilsService
  ) {



  }


  navigateToNewTeam() {
    this._router.navigate([designerPaths.teamManagement, 'new'])
  }

  routeToDetail(rowData: ITeamDto) {
    this._router.navigate([designerPaths.teamManagement, rowData.id])
  }

  remove(rowData: ITeamDto) {


    this._messageBoxService.show({
      buttons: 'YesNo',
      text: 'ss-entities.team.actions.delete.are-you-sure',
      type: 'prompt',
      title: 'ss-entities.team.actions.delete.title',
      replace: {
        '@team': rowData.name
      }
    })
      .pipe(
        take(1),
        filter(answer => answer === 'Yes'),
        switchMap(() => {
          const id = this._loaderService.show({ key: 'team.delete', text: 'ss-entities.team.actions.delete.pending' })
          return this._teamRemoteControllerService.update(rowData.id, { ...rowData, users: [] }).pipe(
            switchMap(() => this._teamRemoteControllerService.remove(rowData.id))
          ).pipe(
            this._loaderService.disposeLoader(id),
            this._snackbarService.handleAsync({
              errorMessage: 'ss-entities.team.actions.delete.error',
              successMessage: 'ss-entities.team.actions.delete.success'
            }),

          )
        }),
        take(1)
      )
      .subscribe(() => {
        this.refreshSubject.next();
      })

  }



  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }

}
